import {
  MEASURE_WINDOWS,
  PORTFOLIO_THREATS,
  SCORE_BANDS,
  threatName,
  type PortfolioPage as PortfolioPageData,
  type PortfolioRow,
  type PortfolioSummary,
} from '@cyberscore/contracts';
import { BarChart3 } from 'lucide-react';
import { useState } from 'react';
import { useListState, usePage, type ListSpec } from '../../lib/list';
import { href } from '../../lib/router';
import { DataView, Meter, type Column, type FilterDef } from '../../ui/DataView';
import { industryLabel } from '../assessments/labels';
import { BAND_FILTER, INDUSTRY_FILTER, STATUS_FILTER, StatusPill, when } from '../clients/parts';
import { useMeasures } from './api';
import { bandLabel, bandRange, bandTone, pct, rm, signed } from './format';

const SPEC: ListSpec = {
  id: 'portfolio',
  sort: 'updated',
  view: 'table',
  filters: ['status', 'band', 'industry', 'exceptions'],
};
const EXCEPTIONS: FilterDef = { key: 'exceptions', label: 'Exceptions', options: [['yes', 'With exceptions'], ['no', 'No exceptions']] }; // prettier-ignore

/**
 * The insurer's portfolio (task 29): every assessment the person can see with its latest stored score,
 * and totals. Read-only: the model recommends, the underwriter decides. The totals cover everything; the
 * list is searched, filtered, sorted and paged on the server (task 60).
 */
export function PortfolioPage() {
  const list = useListState(SPEC);
  const page = usePage<PortfolioRow, Pick<PortfolioPageData, 'summary' | 'attention'>>(
    '/portfolio',
    list.state,
  );
  const data = page.data;

  const columns: Column<PortfolioRow>[] = [
    { key: 'name', label: 'Business', sort: 'name', render: (r) => (<><a href={href.score(r.id)} className="cell-title">{r.customer_name}</a><div className="muted small">{r.title}</div></>) }, // prettier-ignore
    { key: 'industry', label: 'Industry', wide: true, render: (r) => (<>{industryLabel(r.industry)}{r.country && <span className="muted"> · {r.country}</span>}</>) }, // prettier-ignore
    { key: 'score', label: 'CyberScore', sort: 'score', render: (r) => r.score ? (<span className="score-cell"><b>{r.score.score}</b><span className={`pill ${bandTone(r.score.band)}`}>{bandLabel(r.score.band)}</span>{r.score.provisional && <span className="badge" title="Required questions are still open">Provisional</span>}</span>) : <span className="muted">Not scored yet</span> }, // prettier-ignore
    {
      key: 'change',
      label: '30 days',
      sort: 'change',
      align: 'right',
      render: (r) => <Change v={r.score?.change_30d ?? null} />,
    },
    { key: 'evidence', label: 'Verified', sort: 'evidence', render: (r) => r.score ? <Meter value={r.score.evidence_confidence} /> : <span className="muted">–</span> }, // prettier-ignore
    { key: 'exceptions', label: 'Exceptions', render: (r) => <Exceptions r={r} /> },
    ...PORTFOLIO_THREATS.map((t): Column<PortfolioRow> => ({ key: t, label: threatName(t), wide: true, render: (r) => <span className="num">{r.score ? r.score.resilience[t] : '–'}</span> })), // prettier-ignore
    { key: 'loss', label: 'Annual loss', sort: 'loss', align: 'right', wide: true, render: (r) => <span className="num">{r.score?.expected_annual_loss == null ? '–' : rm(r.score.expected_annual_loss)}</span> }, // prettier-ignore
    { key: 'status', label: 'Status', render: (r) => <StatusPill status={r.status} /> },
  ];

  return (
    <>
      <div className="page-head">
        <h1>Portfolio</h1>
        <p>
          Every assessment you can see, with its latest CyberScore. The model recommends; the underwriter
          decides.
        </p>
      </div>

      {data && (
        <>
          <Stats s={data.summary} />
          <div className="pf-grid">
            <section className="card pad">
              <div className="eyebrow">Score distribution</div>
              <p className="muted small">{data.summary.scored} scored assessments by band</p>
              <Bands s={data.summary} />
            </section>
            <section className="card pad">
              <div className="eyebrow">Needs attention</div>
              <p className="muted small">Kill switches not in place cap the score whatever else is in place.</p>
              <Attention rows={data.attention} />
            </section>
          </div>
          <Measures />
        </>
      )}

      <DataView
        list={list}
        query={page}
        noun="assessments"
        rowKey={(r) => r.id}
        rowHref={(r) => href.score(r.id)}
        columns={columns}
        card={(r) => <PortfolioCard r={r} />}
        filters={[STATUS_FILTER, BAND_FILTER, INDUSTRY_FILTER, EXCEPTIONS]}
        sorts={[
          { value: 'updated', label: 'Recent activity', dir: 'desc' },
          { value: 'score', label: 'CyberScore', dir: 'asc' },
          { value: 'change', label: '30-day change', dir: 'asc' },
          { value: 'evidence', label: 'Verified evidence', dir: 'asc' },
          { value: 'loss', label: 'Annual loss', dir: 'desc' },
          { value: 'name', label: 'Business', dir: 'asc' },
        ]}
        searchLabel="Search business or industry"
        empty={<><BarChart3 size={28} /><strong>No assessments yet</strong>Assessments appear here once they are started.</>}
      />
    </>
  ); // prettier-ignore
}

/**
 * Decision relief, measured (task 114, decision 0074): of the Cyber Checks started lately, how many were
 * finished, how long a complete score took, and how many document answers were taken together.
 */
function Measures() {
  const [days, setDays] = useState<number>(30);
  const q = useMeasures(days);
  const m = q.data;
  const hours = (h: number) =>
    h < 1 ? '< 1 h' : h < 48 ? `${Math.round(h)} h` : `${Math.round(h / 24)} days`;
  const stats: [string, string, string][] = m ? [
    ['Started', String(m.started), `Cyber Checks in the last ${m.days} days`],
    ['Finished', m.started ? pct(m.finished / m.started) : '–', `${m.finished} of ${m.started} sent or finished`],
    ['Time to a score', m.median_hours_to_score === null ? '–' : hours(m.median_hours_to_score), m.scored ? `median, over ${m.scored} with a complete score` : 'no complete scores yet'],
    ['Taken together', m.documents.accepted ? pct(m.documents.together / m.documents.accepted) : '–', `of ${m.documents.accepted} document answers used${m.documents.declined ? `, ${m.documents.declined} turned down` : ''}`],
  ] : []; // prettier-ignore
  return (
    <section className="card pad measures">
      <div className="row-between">
        <div>
          <div className="eyebrow">How much we ask of people</div>
          <p className="muted small">Fewer choices should mean more checks finished, sooner.</p>
        </div>
        <div className="seg" role="group" aria-label="Measured over">
          {MEASURE_WINDOWS.map((d) => (
            <button key={d} type="button" aria-pressed={d === days} onClick={() => setDays(d)}>
              {d} days
            </button>
          ))}
        </div>
      </div>
      {q.isError ? (
        <p className="muted small">These measures couldn’t be loaded.</p>
      ) : (
        <div className="measure-grid">
          {stats.map(([label, value, foot]) => (
            <div key={label} className="stat">
              <div className="eyebrow">{label}</div>
              <div className="stat-val">{value}</div>
              <div className="muted small">{foot}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Change({ v }: { v: number | null }) {
  return <span className={`num ${v && v < 0 ? 'down-t' : v ? 'up-t' : 'muted'}`}>{signed(v)}</span>;
}

function Exceptions({ r }: { r: PortfolioRow }) {
  const s = r.score;
  if (!s) return <span className="muted">–</span>;
  return s.exceptions.length ? (
    <span className="pill bad" title={s.exceptions.map((e) => `${e.control} ${e.name}`).join('\n')}>{s.exceptions.length}</span>
  ) : (
    <span className="pill ok">None</span>
  ); // prettier-ignore
}

function PortfolioCard({ r }: { r: PortfolioRow }) {
  const s = r.score;
  return (
    <article className="ccard">
      <div className="ccard-head">
        <div className="minw">
          <h3><a className="cell-title" href={href.score(r.id)}>{r.customer_name}</a></h3>
          <div className="muted small">{industryLabel(r.industry)}{r.country ? ` · ${r.country}` : ''}</div>
        </div>
        <StatusPill status={r.status} />
      </div>
      <div className="ccard-score">
        {s ? (<><b>{s.score}</b><span className={`pill ${bandTone(s.band)}`}>{bandLabel(s.band)}</span>{s.provisional && <span className="badge">Provisional</span>}<Change v={s.change_30d} /></>) : <span className="muted">Not scored yet</span>}
      </div>
      <div className="progress-line">
        <div className="row-between"><span className="muted">Verified evidence</span><span className="num">{s ? pct(s.evidence_confidence) : '–'}</span></div>
        <span className="meter thin"><span className={`fill ${s && s.evidence_confidence >= 0.6 ? 'ok' : 'warn'}`} style={{ width: s ? pct(s.evidence_confidence) : '0%' }} /></span>
      </div>
      <div className="ccard-stats">
        <div><b className={s?.exceptions.length ? 'bad-t' : undefined}>{s ? s.exceptions.length : '–'}</b><span>exceptions</span></div>
        <div><b>{s?.expected_annual_loss == null ? '–' : rm(s.expected_annual_loss)}</b><span>annual loss</span></div>
      </div>
      <div className="ccard-foot">
        <span className="muted">{r.title}</span>
        <span>{when(r.updated_at)}</span>
      </div>
    </article>
  ); // prettier-ignore
}

function Stats({ s }: { s: PortfolioSummary }) {
  const stats: [string, string, string][] = [
    ['Assessments', String(s.assessments), `${s.status.in_progress} in progress · ${s.status.submitted} submitted`],
    ['Average CyberScore', s.average_score === null ? '–' : String(s.average_score), `over ${s.scored} scored${s.provisional ? `, ${s.provisional} provisional` : ''}`],
    ['Verified evidence', s.average_evidence === null ? '–' : pct(s.average_evidence), 'average confidence in the answers'],
    ['Exceptions', String(s.exceptions), `kill switches not in place, in ${s.with_exceptions} ${s.with_exceptions === 1 ? 'assessment' : 'assessments'}`],
    ['Modelled annual loss', s.loss_modelled ? rm(s.expected_annual_loss) : '–', s.loss_modelled ? `expected, over ${s.loss_modelled} with revenue known` : 'needs revenue in the answers'],
  ]; // prettier-ignore
  return (
    <div className="stat-grid">
      {stats.map(([label, value, foot]) => (
        <section key={label} className="card stat">
          <div className="eyebrow">{label}</div>
          <div className="stat-val">{value}</div>
          <div className="muted small">{foot}</div>
        </section>
      ))}
    </div>
  );
}

function Bands({ s }: { s: PortfolioSummary }) {
  const most = Math.max(1, ...Object.values(s.bands));
  return (
    <ol className="band-bars">
      {SCORE_BANDS.map((b) => (
        <li key={b.key}>
          <span className="band-name">
            {bandLabel(b.key)} <span className="muted small">{bandRange(b.key)}</span>
          </span>
          <span className="meter">
            <span
              className={`fill ${bandTone(b.key)}`}
              style={{ width: `${(s.bands[b.key] / most) * 100}%` }}
            />
          </span>
          <b>{s.bands[b.key]}</b>
        </li>
      ))}
    </ol>
  );
}

function Attention({ rows }: { rows: PortfolioRow[] }) {
  if (!rows.length)
    return <p className="muted">No exceptions: every scored assessment passes its kill switches.</p>;
  return (
    <ul className="attention">
      {rows.map((r) => (
        <li key={r.id}>
          <a href={href.score(r.id)}>
            <b>{r.customer_name}</b>
            <span className="muted small">
              {r.score!.exceptions.map((e) => `${e.name} (${e.control})`).join(' · ')}
            </span>
          </a>
          <span className="pill bad">capped at {r.score!.cap}</span>
        </li>
      ))}
    </ul>
  ); // prettier-ignore
}
